import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  getSupabaseAdminClient,
  type ProductRecommendation,
  type RecommendationStats,
} from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

interface Props {
  recommendation: ProductRecommendation;
  onClose: () => void;
}

export function RecommendationStatsModal({ recommendation, onClose }: Props) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['recommendation-stats', recommendation.id],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_recommendation_stats', {
        p_recommendation_id: recommendation.id,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? { impressions: 0, clicks: 0 }) as RecommendationStats;
    },
  });

  const ctr = stats && stats.impressions > 0 ? ((stats.clicks / stats.impressions) * 100).toFixed(1) : '0.0';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Statistik</h2>
            <p className="text-sm text-slate-500 truncate">{recommendation.title}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {isLoading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-16 bg-slate-100 rounded"></div>
            <div className="h-16 bg-slate-100 rounded"></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase">Impressions</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{stats?.impressions ?? 0}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase">Klicks</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{stats?.clicks ?? 0}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase">CTR</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{ctr}%</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
